import Button from 'devextreme-react/button';
import DataGrid, {
  Column,
  ColumnChooser,
  HeaderFilter,
  Item,
  Scrolling,
  SearchPanel,
  Toolbar,
  type DataGridRef
} from 'devextreme-react/data-grid';
import TextBox from 'devextreme-react/text-box';
import CheckBox from 'devextreme-react/check-box';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  GridDetailField,
  GridDetailPopup,
  GridDetailSection,
  GridDetailValue,
  GridFormPopup,
  OPS_SITE_GRID_PROPS,
  OpsGridStateStoring,
  OpsGridToolbarItems,
  onGridFocusedRowEnter,
  onGridRowDblClick,
  opsGridDetailPopupSizeKey,
  opsGridFormPopupSizeKey
} from '@jarvis/platform-ui';
import {
  listSyncJobs,
  triggerSyncJob,
  updateSyncJob,
  type SyncJob
} from '../../../api/syncJobs';
import { showToast } from '../../../notifications/toast';
import { formatLocalDateTime } from '../../../utils/timezone';

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  return formatLocalDateTime(value) || '—';
}

const STATUS_TONE_FG: Record<string, string> = {
  Succeeded: 'var(--ops-status-succeeded-fg)',
  PartialSuccess: 'var(--ops-status-partial-fg)',
  Failed: 'var(--ops-status-failed-fg)',
  Running: 'var(--ops-status-running-fg)',
  Cancelled: 'var(--ops-status-cancelled-fg)'
};

const GRID_STATE_KEY = 'webopsui:sync-jobs-grid:v2';
const SYNC_JOBS_GRID_COLUMNS = ['displayName', 'name', 'cronSchedule', 'enabled'] as const;

interface EditState {
  job: SyncJob;
  cron: string;
  enabled: boolean;
  displayName: string;
  description: string;
  saving: boolean;
  error: string | null;
}

function SyncJobDetailView({ job }: { job: SyncJob }) {
  const lastStatus = job.lastRun?.status ?? '—';
  const statusColor = STATUS_TONE_FG[lastStatus] ?? 'var(--ops-status-muted-fg)';

  return (
    <>
      <div>
        <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--ops-color-text, #111827)' }}>
          {job.displayName}
        </div>
        <div style={{ marginTop: 10 }}>
          <span
            style={{
              fontWeight: 600,
              color: job.enabled ? 'var(--ops-color-success)' : 'var(--ops-color-danger)'
            }}
          >
            {job.enabled ? 'Enabled' : 'Paused'}
          </span>
        </div>
        <div style={{ marginTop: 8, fontSize: 12, color: 'var(--ops-color-muted, #64748b)', fontFamily: 'monospace' }}>
          {job.name}
        </div>
      </div>

      <GridDetailSection title="Schedule">
        <div style={{ display: 'grid', gap: '0.65rem' }}>
          <GridDetailField label="Cron">{job.cronSchedule}</GridDetailField>
          <GridDetailField label="Next run">{formatDate(job.nextFireTimeUtc)}</GridDetailField>
        </div>
      </GridDetailSection>

      <GridDetailSection title="Last run">
        <div style={{ display: 'grid', gap: '0.65rem' }}>
          <GridDetailField label="Status">
            <span style={{ fontWeight: 600, color: statusColor }}>{lastStatus}</span>
          </GridDetailField>
          <GridDetailField label="Started">{formatDate(job.lastRun?.startedAt ?? null)}</GridDetailField>
        </div>
      </GridDetailSection>

      <GridDetailSection title="Description">
        <GridDetailValue muted={!job.description}>{job.description?.trim() || '—'}</GridDetailValue>
      </GridDetailSection>
    </>
  );
}

export function SyncJobsPage() {
  const navigate = useNavigate();
  const gridRef = useRef<DataGridRef>(null);
  const [jobs, setJobs] = useState<SyncJob[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewJob, setViewJob] = useState<SyncJob | null>(null);
  const [edit, setEdit] = useState<EditState | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setJobs(await listSyncJobs());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load sync jobs.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (error) showToast(error, 'error');
  }, [error]);

  const resetGridLayout = useCallback(() => {
    gridRef.current?.instance()?.state(null);
    try {
      localStorage.removeItem(GRID_STATE_KEY);
    } catch {
      /* localStorage may be unavailable */
    }
    showToast('Grid layout reset to defaults.', 'info');
  }, []);

  function openEdit(job: SyncJob) {
    setViewJob(null);
    setEdit({
      job,
      cron: job.cronSchedule,
      enabled: job.enabled,
      displayName: job.displayName,
      description: job.description ?? '',
      saving: false,
      error: null
    });
  }

  function beginEditFromView() {
    if (!viewJob) return;
    openEdit(viewJob);
  }

  async function handleSave() {
    if (!edit) return;
    setEdit({ ...edit, saving: true, error: null });
    try {
      await updateSyncJob(edit.job.name, {
        cronSchedule: edit.cron,
        enabled: edit.enabled,
        displayName: edit.displayName,
        description: edit.description
      });
      setEdit(null);
      await refresh();
    } catch (err) {
      setEdit((current) => (current ? { ...current, saving: false, error: err instanceof Error ? err.message : 'Failed to save.' } : current));
    }
  }

  async function handleTrigger(job: SyncJob) {
    setError(null);
    try {
      await triggerSyncJob(job.name);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to trigger job.');
    }
  }

  function renderEditContent() {
    if (!edit) return null;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        <label style={{ fontSize: 12, fontWeight: 600 }}>Display name</label>
        <TextBox value={edit.displayName} onValueChanged={(e) => setEdit({ ...edit, displayName: e.value ?? '' })} />

        <label style={{ fontSize: 12, fontWeight: 600 }}>Cron schedule (Quartz)</label>
        <TextBox value={edit.cron} onValueChanged={(e) => setEdit({ ...edit, cron: e.value ?? '' })} placeholder="0 0/15 * * * ?" />

        <label style={{ fontSize: 12, fontWeight: 600 }}>Description</label>
        <TextBox value={edit.description} onValueChanged={(e) => setEdit({ ...edit, description: e.value ?? '' })} />

        <CheckBox value={edit.enabled} text="Enabled (run on schedule)" onValueChanged={(e) => setEdit({ ...edit, enabled: Boolean(e.value) })} />
      </div>
    );
  }

  return (
    <div className="ops-fill-page ops-fill-page--bare">
      <div className="ops-fill-page__grid-host">
        <DataGrid
          ref={gridRef}
          dataSource={jobs}
          keyExpr="name"
          height="100%"
          showBorders
          columnAutoWidth
          columnHidingEnabled
          allowColumnResizing
          allowColumnReordering
          columnResizingMode="widget"
          {...OPS_SITE_GRID_PROPS}
          wordWrapEnabled
          onRowDblClick={(e) => onGridRowDblClick(e, setViewJob)}
          onKeyDown={(e) => onGridFocusedRowEnter(e, setViewJob)}
        >
          <OpsGridStateStoring
            storageKey={GRID_STATE_KEY}
            schemaVersion={1}
            columns={SYNC_JOBS_GRID_COLUMNS}
          />
          <Toolbar>
            <OpsGridToolbarItems
              gridRef={gridRef}
              loading={loading}
              onRefresh={() => void refresh()}
              refreshHint="Refresh jobs"
              export={{ fileName: 'sync-jobs-export', worksheetName: 'Sync jobs', hint: 'Export jobs' }}
              resetLayout={{ onReset: resetGridLayout }}
            />
            <Item name="searchPanel" location="after" />
          </Toolbar>
          <SearchPanel visible width={220} />
          <HeaderFilter visible />
          <ColumnChooser enabled mode="select" allowSearch title="Choose columns" />
          <Scrolling mode="standard" scrollByThumb showScrollbar="onScroll" useNative />
          <Column name="displayName" dataField="displayName" caption="Job" minWidth={180} />
          <Column name="name" dataField="name" caption="Key" width={160} />
          <Column name="cronSchedule" dataField="cronSchedule" caption="Cron" width={170} />
          <Column
            name="enabled"
            dataField="enabled"
            caption="Enabled"
            dataType="boolean"
            width={110}
            trueText="Enabled"
            falseText="Paused"
            cellRender={(c) => (
              <span
                style={{ fontWeight: 600, color: c.value ? 'var(--ops-color-success)' : 'var(--ops-color-danger)' }}
              >
                {c.value ? 'Enabled' : 'Paused'}
              </span>
            )}
          />
          <Column
            caption="Last run"
            allowHeaderFiltering={false}
            allowFiltering={false}
            calculateCellValue={(row: SyncJob) => row.lastRun?.status ?? '—'}
            cellRender={(c) => {
              const status = c.value as string;
              const colour = STATUS_TONE_FG[status] ?? 'var(--ops-status-muted-fg)';
              return <span style={{ fontWeight: 600, color: colour }}>{status}</span>;
            }}
            width={130}
          />
          <Column
            caption="Last run at"
            allowHeaderFiltering={false}
            allowFiltering={false}
            calculateCellValue={(row: SyncJob) => row.lastRun?.startedAt ?? null}
            calculateDisplayValue={(row: SyncJob) => formatDate(row.lastRun?.startedAt ?? null)}
          />
          <Column
            caption="Next run"
            allowHeaderFiltering={false}
            allowFiltering={false}
            calculateCellValue={(row: SyncJob) => row.nextFireTimeUtc}
            calculateDisplayValue={(row: SyncJob) => formatDate(row.nextFireTimeUtc)}
          />
          <Column
            caption="Actions"
            cellRender={(c) => {
              const job = c.data as SyncJob;
              return (
                <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                  <Button
                    text="Edit"
                    icon="edit"
                    stylingMode="text"
                    elementAttr={{ 'data-testid': `edit-sync-job-${job.name}` }}
                    onClick={() => openEdit(job)}
                  />
                  <Button text="Trigger" icon="event" stylingMode="text" onClick={() => void handleTrigger(job)} />
                  <Button text="Runs" icon="bulletlist" stylingMode="text" onClick={() => navigate(`/admin/sync-jobs/${encodeURIComponent(job.name)}/runs`)} />
                </div>
              );
            }}
            allowFiltering={false}
            allowSorting={false}
            width={260}
          />
        </DataGrid>
      </div>

      <GridDetailPopup
        visible={viewJob !== null}
        title="Sync job details"
        onClose={() => setViewJob(null)}
        width={560}
        maxBodyHeight={440}
        sizeStorageKey={opsGridDetailPopupSizeKey(GRID_STATE_KEY)}
        primaryActions={[
          {
            text: 'Trigger now',
            icon: 'event',
            stylingMode: 'outlined',
            onClick: () => {
              if (viewJob) void handleTrigger(viewJob);
            }
          },
          {
            text: 'Edit',
            icon: 'edit',
            type: 'default',
            stylingMode: 'contained',
            onClick: beginEditFromView
          }
        ]}
      >
        {viewJob ? <SyncJobDetailView job={viewJob} /> : null}
      </GridDetailPopup>

      {edit ? (
        <GridFormPopup
          visible
          title={`Edit ${edit.job.displayName}`}
          onClose={() => setEdit(null)}
          width={520}
          maxBodyHeight={440}
          sizeStorageKey={opsGridFormPopupSizeKey(GRID_STATE_KEY)}
          error={edit.error}
          footer={
            <>
              <Button text="Cancel" onClick={() => setEdit(null)} disabled={edit.saving} />
              <Button
                text="Save"
                type="default"
                stylingMode="contained"
                onClick={() => void handleSave()}
                disabled={edit.saving}
              />
            </>
          }
        >
          {renderEditContent()}
        </GridFormPopup>
      ) : null}
    </div>
  );
}
